import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Award, Plus, X, Trophy, Target, Calendar } from 'lucide-react';

interface TrainingSectionProps {
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
}

export const TrainingSection = ({ data, onChange }: TrainingSectionProps) => {
  const handleChange = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const addCompetition = () => {
    const events = data.events || [];
    events.push({
      date: '',
      name: '',
      organization: '',
      discipline: '',
      class: '',
      location: '',
      result: {
        placing: '',
        score: '',
        judge: ''
      }
    });
    handleChange('events', events);
  };

  const addTrainingSession = () => {
    const sessions = data.training_sessions || [];
    sessions.push({
      activity: '',
      start_date: '',
      end_date: '',
      frequency: 'weekly',
      days: [],
      monthly_day: '',
      custom_dates: [],
      duration_minutes: '',
      trainer: '',
      notes: ''
    });
    handleChange('training_sessions', sessions);
  };

  const updateTrainingSession = (index: number, field: string, value: any) => {
    const sessions = [...(data.training_sessions || [])];
    sessions[index] = { ...sessions[index], [field]: value };
    handleChange('training_sessions', sessions);
  };

  const removeTrainingSession = (index: number) => {
    const sessions = data.training_sessions || [];
    sessions.splice(index, 1);
    handleChange('training_sessions', sessions);
  };

  const updateCompetition = (index: number, field: string, value: any) => {
    const events = [...(data.events || [])];
    if (field.startsWith('result.')) {
      const resultField = field.split('.')[1];
      events[index] = { 
        ...events[index], 
        result: { 
          ...events[index].result, 
          [resultField]: value 
        } 
      };
    } else {
      events[index] = { ...events[index], [field]: value };
    }
    handleChange('events', events);
  };

  const removeCompetition = (index: number) => {
    const events = data.events || [];
    events.splice(index, 1);
    handleChange('events', events);
  };

  const addTitle = () => {
    const titles = data.titles_awards || [];
    titles.push({
      title: '',
      awarding_body: '',
      achieved_on: '',
      description: ''
    });
    handleChange('titles_awards', titles);
  };

  const updateTitle = (index: number, field: string, value: string) => {
    const titles = [...(data.titles_awards || [])];
    titles[index] = { ...titles[index], [field]: value };
    handleChange('titles_awards', titles);
  };

  const removeTitle = (index: number) => {
    const titles = data.titles_awards || [];
    titles.splice(index, 1);
    handleChange('titles_awards', titles);
  };

  const disciplines = [
    'Agility', 'Obedience', 'Rally-O', 'IGP', 'Mondioring', 'PSA', 
    'Flyball', 'Nosework', 'Coursing', 'Canicross', 'Sleddog', 
    'Esposizione', 'Dog Dancing', 'Disc Dog', 'Water Work'
  ];

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center gap-3 mb-6">
        <Award className="w-6 h-6 text-purple-500" />
        <h2 className="text-2xl font-bold text-white">Addestramento e Competizioni</h2>
      </div>

      {/* Training Skills */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Target className="w-5 h-5" />
            Abilità e Addestramento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="skills" className="text-white">Abilità Acquisite</Label>
            <Textarea
              id="skills"
              value={data.skills || ''}
              onChange={(e) => handleChange('skills', e.target.value)}
              placeholder="Lista delle abilità apprese: seduto, terra, resta, richiamo..."
              className="mt-1"
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="training_level" className="text-white">Livello di Addestramento</Label>
              <Select value={data.training_level || ''} onValueChange={(value) => handleChange('training_level', value)}>
                <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                  <SelectValue placeholder="Seleziona livello" />
                </SelectTrigger>
                <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                  <SelectItem value="beginner">Principiante</SelectItem>
                  <SelectItem value="intermediate">Intermedio</SelectItem>
                  <SelectItem value="advanced">Avanzato</SelectItem>
                  <SelectItem value="expert">Esperto</SelectItem>
                  <SelectItem value="professional">Professionale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="trainer" className="text-white">Addestratore</Label>
              <Input
                id="trainer"
                value={data.trainer || ''}
                onChange={(e) => handleChange('trainer', e.target.value)}
                placeholder="Nome dell'addestratore"
                className="mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="training_notes" className="text-white">Note sull'Addestramento</Label>
            <Textarea
              id="training_notes"
              value={data.training_notes || ''}
              onChange={(e) => handleChange('training_notes', e.target.value)}
              placeholder="Metodi utilizzati, progressi, comportamenti da migliorare..."
              className="mt-1"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Competitions and Events */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Gare e Eventi
            </div>
            <Button onClick={addCompetition} size="sm" variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(data.events || []).map((event: any, index: number) => (
            <div key={index} className="p-4 border border-white/10 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-white">Evento #{index + 1}</h4>
                <Button
                  onClick={() => removeCompetition(index)}
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Data</Label>
                  <Input
                    type="date"
                    value={event.date}
                    onChange={(e) => updateCompetition(index, 'date', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Nome Evento</Label>
                  <Input
                    value={event.name}
                    onChange={(e) => updateCompetition(index, 'name', e.target.value)}
                    placeholder="Nome della competizione"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Organizzazione</Label>
                  <Input
                    value={event.organization}
                    onChange={(e) => updateCompetition(index, 'organization', e.target.value)}
                    placeholder="ENCI, FCI, Club locale..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Disciplina</Label>
                  <Select 
                    value={event.discipline} 
                    onValueChange={(value) => updateCompetition(index, 'discipline', value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleziona disciplina" />
                    </SelectTrigger>
                    <SelectContent>
                      {disciplines.map((discipline) => (
                        <SelectItem key={discipline} value={discipline}>
                          {discipline}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Classe</Label>
                  <Input
                    value={event.class}
                    onChange={(e) => updateCompetition(index, 'class', e.target.value)}
                    placeholder="Categoria di gara"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Località</Label>
                  <Input
                    value={event.location}
                    onChange={(e) => updateCompetition(index, 'location', e.target.value)}
                    placeholder="Città, sede"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Piazzamento</Label>
                  <Input
                    value={event.result?.placing || ''}
                    onChange={(e) => updateCompetition(index, 'result.placing', e.target.value)}
                    placeholder="1°, 2°, 3°..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Punteggio</Label>
                  <Input
                    value={event.result?.score || ''}
                    onChange={(e) => updateCompetition(index, 'result.score', e.target.value)}
                    placeholder="Punti ottenuti"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white">Giudice</Label>
                  <Input
                    value={event.result?.judge || ''}
                    onChange={(e) => updateCompetition(index, 'result.judge', e.target.value)}
                    placeholder="Nome del giudice"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          ))}
          
          {(!data.events || data.events.length === 0) && (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessun evento registrato</p>
              <p className="text-sm">Clicca "Aggiungi" per registrare gare e competizioni</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Titles and Awards */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Titoli e Riconoscimenti
            </div>
            <Button onClick={addTitle} size="sm" variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(data.titles_awards || []).map((title: any, index: number) => (
            <div key={index} className="p-4 border border-white/10 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-white">Titolo #{index + 1}</h4>
                <Button
                  onClick={() => removeTitle(index)}
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Titolo</Label>
                  <Input
                    value={title.title}
                    onChange={(e) => updateTitle(index, 'title', e.target.value)}
                    placeholder="es. Campione Sociale, CH, IPO-1"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Ente Riconoscente</Label>
                  <Input
                    value={title.awarding_body}
                    onChange={(e) => updateTitle(index, 'awarding_body', e.target.value)}
                    placeholder="ENCI, FCI, Club..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Data Conseguimento</Label>
                  <Input
                    type="date"
                    value={title.achieved_on}
                    onChange={(e) => updateTitle(index, 'achieved_on', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white">Descrizione</Label>
                  <Textarea
                    value={title.description}
                    onChange={(e) => updateTitle(index, 'description', e.target.value)}
                    placeholder="Dettagli sul titolo conseguito..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          ))}
          
          {(!data.titles_awards || data.titles_awards.length === 0) && (
            <div className="text-center py-8 text-muted-foreground">
              <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessun titolo registrato</p>
              <p className="text-sm">Clicca "Aggiungi" per registrare titoli e riconoscimenti</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Training Sessions */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Sessioni di Addestramento
            </div>
            <Button onClick={addTrainingSession} size="sm" variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Sessione
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(data.training_sessions || []).map((session: any, index: number) => (
            <div key={index} className="p-4 border border-white/10 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-white">Sessione #{index + 1}</h4>
                <Button
                  onClick={() => removeTrainingSession(index)}
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Attività di Addestramento</Label>
                  <Input
                    value={session.activity || ''}
                    onChange={(e) => updateTrainingSession(index, 'activity', e.target.value)}
                    placeholder="es. Obbedienza di base, Agility"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Durata (minuti)</Label>
                  <Input
                    type="number"
                    min="5"
                    value={session.duration_minutes || ''}
                    onChange={(e) => updateTrainingSession(index, 'duration_minutes', e.target.value)}
                    placeholder="30"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Data Inizio</Label>
                  <Input
                    type="date"
                    value={session.start_date || ''}
                    onChange={(e) => updateTrainingSession(index, 'start_date', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Data Fine (opzionale)</Label>
                  <Input
                    type="date"
                    value={session.end_date || ''}
                    onChange={(e) => updateTrainingSession(index, 'end_date', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Frequenza</Label>
                  <Select value={session.frequency || 'weekly'} onValueChange={(value) => updateTrainingSession(index, 'frequency', value)}>
                    <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                      <SelectValue placeholder="Frequenza" />
                    </SelectTrigger>
                    <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                      <SelectItem value="daily">Giornaliero</SelectItem>
                      <SelectItem value="weekly">Settimanale</SelectItem>
                      <SelectItem value="monthly">Mensile</SelectItem>
                      <SelectItem value="specific">Date Specifiche</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Addestratore</Label>
                  <Input
                    value={session.trainer || ''}
                    onChange={(e) => updateTrainingSession(index, 'trainer', e.target.value)}
                    placeholder="Nome addestratore"
                    className="mt-1"
                  />
                </div>
                
                {session.frequency === 'weekly' && (
                  <div className="md:col-span-2">
                    <Label className="text-white">Giorni della Settimana</Label>
                    <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mt-2">
                      {['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'].map((day, dayIndex) => (
                        <label key={day} className="flex items-center gap-1 text-sm text-white">
                          <input
                            type="checkbox"
                            checked={(session.days || []).includes(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex])}
                            onChange={(e) => {
                              const englishDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                              const currentDays = session.days || [];
                              const englishDay = englishDays[dayIndex];
                              if (e.target.checked) {
                                updateTrainingSession(index, 'days', [...currentDays, englishDay]);
                              } else {
                                updateTrainingSession(index, 'days', currentDays.filter((d: string) => d !== englishDay));
                              }
                            }}
                          />
                          {day.slice(0, 3)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {session.frequency === 'monthly' && (
                  <div>
                    <Label className="text-white">Giorno del Mese</Label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={session.monthly_day || ''}
                      onChange={(e) => updateTrainingSession(index, 'monthly_day', e.target.value)}
                      placeholder="es. 15 (ogni 15 del mese)"
                      className="mt-1"
                    />
                  </div>
                )}
                
                {session.frequency === 'specific' && (
                  <div className="md:col-span-2">
                    <Label className="text-white">Date Specifiche</Label>
                    <div className="space-y-2 mt-2">
                      {(session.custom_dates || []).map((date: string, dateIndex: number) => (
                        <div key={dateIndex} className="flex items-center gap-2">
                          <Input
                            type="date"
                            value={date}
                            onChange={(e) => {
                              const newDates = [...(session.custom_dates || [])];
                              newDates[dateIndex] = e.target.value;
                              updateTrainingSession(index, 'custom_dates', newDates);
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newDates = session.custom_dates || [];
                              newDates.splice(dateIndex, 1);
                              updateTrainingSession(index, 'custom_dates', newDates);
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const currentDates = session.custom_dates || [];
                          updateTrainingSession(index, 'custom_dates', [...currentDates, '']);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Aggiungi Data
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="md:col-span-2">
                  <Label className="text-white">Note Sessione</Label>
                  <Textarea
                    value={session.notes || ''}
                    onChange={(e) => updateTrainingSession(index, 'notes', e.target.value)}
                    placeholder="Obiettivi, progressi, osservazioni..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          ))}
          
          {(!data.training_sessions || data.training_sessions.length === 0) && (
            <div className="text-center py-8 text-muted-foreground">
              <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessuna sessione di addestramento programmata</p>
              <p className="text-sm">Clicca "Aggiungi Sessione" per creare un piano di addestramento</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Behavioral Notes */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Note Comportamentali</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="temperament" className="text-white">Temperamento</Label>
            <Textarea
              id="temperament"
              value={data.temperament || ''}
              onChange={(e) => handleChange('temperament', e.target.value)}
              placeholder="Descrizione del carattere e temperamento..."
              className="mt-1"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="behavioral_notes" className="text-white">Note Comportamentali</Label>
            <Textarea
              id="behavioral_notes"
              value={data.behavioral_notes || ''}
              onChange={(e) => handleChange('behavioral_notes', e.target.value)}
              placeholder="Comportamenti particolari, reazioni, abitudini..."
              className="mt-1"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};